import LoadingAnimation from 'components/LoadingAnimation'
import PMBPagination from 'components/PMBPagination'
import PMBSearchInputGroup from 'components/PMBSearchInputGroup'
import { setSelectedSchema } from 'features/public-views/login/loginSlice'
import { useDebounce } from 'helpers'
import { useEffect, useState } from 'react'
import { ChevronLeft, ChevronRight, Settings } from 'react-feather'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import { Button, Card, CardBody, Container } from 'reactstrap'
import { URL_FORM_PROCESS, URL_SESSION_PROCESS } from 'utils/pathUrl'
import {
  useFetchSchemaQuery,
  useLazyFetchSessionQuery,
} from '../processApiSlice'
import { setFetchOption, setSession } from '../processSlice'

const ListProcess = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const location = useLocation()
  const dataState = location.state

  const { tag } = useSelector((s) => s.process)
  const [searchInput, setSearchInput] = useState(tag?.s || '')

  const debouncedSearch = useDebounce((value) => {
    dispatch(
      setFetchOption({
        s: value || undefined,
        page: 0,
      }),
    )
  }, 600)

  const onHandleSearchChange = (value) => {
    setSearchInput(value ?? '')
    debouncedSearch(value ?? '')
  }

  const handleKeyDown = (e) => {
    if (e.key === ' ' && searchInput.length === 0) {
      e.preventDefault()
    }
  }

  const { sessionProcess } = useSelector((s) => s.auth)

  const [triggerFetchSession] = useLazyFetchSessionQuery()
  const { data, isLoading, status } = useFetchSchemaQuery({ ...tag })

  useEffect(() => {
    const checkSession = async () => {
      if (!sessionProcess) {
        navigate(URL_SESSION_PROCESS)
        return
      }

      if (sessionProcess.isAutoCloseOnEOD) {
        const createdDay = new Date(sessionProcess.createdAt).toDateString()
        const today = new Date().toDateString()

        if (createdDay !== today) {
          try {
            const res = await triggerFetchSession()
            const freshSession = res?.data
            if (!freshSession?.id) {
              dispatch(setSession(null))
              navigate(URL_SESSION_PROCESS)
            } else {
              dispatch(setSession(freshSession))
            }
          } catch (err) {
            console.error('Failed to fetch session:', err)
            dispatch(setSession(null))
            navigate(URL_SESSION_PROCESS)
          }
        }
      }
    }

    checkSession()
  }, [sessionProcess])

  return (
    <div className="pb-3 px-3 w-100">
      <Container className="custom-container">
        {/* Title */}
        <h5 className="fw-bold mb-3">Pilih Proses</h5>

        {/* Search */}
        <div className="mb-3">
          <PMBSearchInputGroup
            placeholder="Cari nama proses..."
            value={searchInput}
            onChange={(e) => onHandleSearchChange(e.target.value)}
            onKeyDown={handleKeyDown}
            maxWidth="100%"
            testId="process-search-input"
          />
        </div>

        {/* Process List */}
        <div className="d-flex flex-column gap-3">
          {isLoading || status === 'pending' ? (
            <LoadingAnimation />
          ) : (
            <div className="d-flex flex-column gap-3">
              {data?.items?.length ? (
                data.items.map((item) => (
                  <Card
                    key={item.id}
                    className="shadow-sm border-0 rounded-3"
                    data-testid={`process-card-${item.id}`}
                  >
                    <CardBody className="py-3">
                      <div className="d-flex justify-content-between align-items-center">
                        <div className="d-flex align-items-center gap-3">
                          <div className="bg-light p-3 rounded">
                            <Settings size={30} className="text-secondary" />
                          </div>

                          <div>
                            <div className="fw-semibold">{item?.label}</div>
                            <div className="text-muted small">
                              <div className="badge rounded-pill bg-gray-100 text-gray-700 px-2 border border-success">
                                {item?.numInput} input
                              </div>{' '}
                              <div className="badge rounded-pill text-gray-700 px-2 border border-success">
                                {item?.numOutput} output
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="d-flex gap-2 mt-3 flex-wrap">
                        <Button
                          color="primary"
                          outline
                          className="flex-grow-1"
                          size="sm"
                          onClick={() => {
                            dispatch(setSelectedSchema(item))
                            navigate(URL_FORM_PROCESS, {
                              state: { processDirection: 'forward' },
                            })
                          }}
                          data-testid={`process-forward-${item.id}`}
                        >
                          <ChevronRight size={14} className="me-1" />
                          Kedepan
                        </Button>
                        <Button
                          color="primary"
                          className="flex-grow-1"
                          size="sm"
                          onClick={() => {
                            dispatch(setSelectedSchema(item))
                            navigate(URL_FORM_PROCESS, {
                              state: { processDirection: 'backward' },
                            })
                          }}
                          data-testid={`process-backward-${item.id}`}
                        >
                          <ChevronLeft size={14} className="me-1" />
                          Kebelakang
                        </Button>
                      </div>
                    </CardBody>
                  </Card>
                ))
              ) : (
                <div className="text-center py-5 text-muted">
                  <Settings size={48} className="mb-3" />
                  <p className="mb-0">Belum ada proses yang tersedia</p>
                </div>
              )}
            </div>
          )}
          <PMBPagination
            rowCount={data?.totalItems || 0}
            currentPage={data?.currentPage || 0}
            rowsPerPage={tag?.size || 10}
            setCurrentPage={(e) => {
              dispatch(
                setFetchOption({
                  page: e,
                }),
              )
            }}
            setRowsPerPage={(e) => {
              dispatch(
                setFetchOption({
                  size: e,
                }),
              )
            }}
            testId="process-pagination"
          />
        </div>
      </Container>
    </div>
  )
}

export default ListProcess
